/**
 * The billing exception queue (KAN-53).
 *
 * The working screen for Legal Production and Finance both, and the two see
 * different rows from the same endpoint — Finance the firm, Legal Production its
 * own matters plus any unassigned time it recorded. The scoping happens
 * server-side; this page renders what it is given and says which of the two it
 * is looking at.
 *
 * A resolved exception leaves the default list and stays readable behind the
 * status filter, because who cleared a finding and why is part of the story of
 * the bill that followed.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BILLING_EXCEPTION_CONFIDENCE_LABELS,
  BILLING_EXCEPTION_META,
  BILLING_EXCEPTION_RESOLUTION_META,
  BILLING_EXCEPTION_TYPES,
  BILL_STATUSES,
  BILL_STATUS_META,
  DETECTED_BILL_STATUSES,
  HUMAN_RESOLUTIONS,
  MATTER_FEE_TYPE_LABELS,
  MATTER_RATE_SUBJECT_LABELS,
  type BillStatus,
  type BillingExceptionResolution,
  type BillingExceptionStatus,
  type BillingExceptionConfidence,
  type BillingExceptionType,
  type BillingRuleSettings,
  type DescriptionFlag,
  type HumanResolution,
  type MatterFeeType,
  type MatterRateSubject,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { formatDateTime, formatHours, formatMoney, formatRelative } from '../lib/format'
import { useSession } from '../lib/session'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'
import { SyncIcon } from '../ui/icons'

interface ExceptionRow {
  id: string
  exceptionType: BillingExceptionType
  status: BillingExceptionStatus
  /** Duplicate detection only. Null on every other rule — see the shared type. */
  confidence: BillingExceptionConfidence | null
  /** Rule-specific payload: the flagged text, the day that ran long. */
  detail: Record<string, unknown> | null
  expectedRateMinor: number | null
  actualRateMinor: number | null
  amountMinor: number | null
  detectedAt: string
  lastSeenAt: string
  resolution: BillingExceptionResolution | null
  resolutionNote: string | null
  resolvedAt: string | null
  resolvedByName: string | null
  timeEntry: {
    id: string
    description: string | null
    durationSeconds: number
    rateMinor: number | null
    workedAt: string
    isBillable: boolean
    /** Where the entry stands relative to a bill — unbilled, draft, or billed. */
    billStatus: BillStatus
    invoiceNumber: string | null
    invoiceSourceId: string | null
    sourceSystem: string
    sourceId: string | null
  } | null
  matter: {
    id: string
    displayNumber: string
    description: string | null
    retainerRateMinor: number | null
    feeType: MatterFeeType
    clientName: string | null
    sourceId: string | null
    /** The matter's agreed rate table from Clio, whole — see the serializer. */
    rates: {
      subject: MatterRateSubject
      attorneyId: string | null
      attorneyName: string | null
      roleLabel: string | null
      rateMinor: number
    }[]
    hasAgreedRateForAttorney: boolean
  } | null
  attorney: { id: string; name: string } | null
  duplicateOf: {
    id: string
    workedAt: string
    description: string | null
    sourceId: string | null
  } | null
}

interface ExceptionPage {
  meta: { total: number; perPage: number; currentPage: number; lastPage: number }
  scope: { all: boolean; actingAsAttorneyId: string | null }
  exceptions: ExceptionRow[]
}

interface AttorneyTally {
  attorneyId: string | null
  name: string | null
  open: number
  amountMinor: number
}

interface Summary {
  open: number
  counts: Record<BillingExceptionType, { open: number; resolved: number }>
  /** Open findings per timekeeper, biggest first — the "group by user" ask. */
  byAttorney: AttorneyTally[]
  blockedMinor: number
  lastDetectedAt: string | null
}

interface DetectionResult {
  opened: number
  reopened: number
  unchanged: number
  autoResolved: number
  superseded: number
  entriesScanned: number
  /**
   * Billable entries on hourly matters with no agreed rate to compare against.
   * Reported because "no rate mismatches" and "nothing to compare" look
   * identical from the queue and mean opposite things.
   */
  ratesUnmatched: number
}

const TYPE_TONES: Record<BillingExceptionType, Tone> = {
  no_matter_assigned: 'critical',
  rate_mismatch: 'warning',
  missing_description: 'brand',
  possible_duplicate: 'accent',
  daily_hours_exceeded: 'warning',
  inconsistent_rate: 'warning',
  /**
   * Neutral on purpose. This is the one finding that is not a defect — the
   * matter is simply not worth billing yet — and dressing it in the same colour
   * as a rate error would teach people to ignore that colour.
   */
  wip_below_threshold: 'neutral',
}

const BILL_STATUS_TONES: Record<BillStatus, Tone> = {
  unbilled: 'neutral',
  draft: 'brand',
  billed: 'positive',
}

export function BillingExceptions() {
  const { may } = useSession()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [type, setType] = useState('')
  const [status, setStatus] = useState<'open' | 'resolved' | 'all'>('open')
  /** Work dates, as `YYYY-MM-DD` — the format the date input speaks. */
  const [workedFrom, setWorkedFrom] = useState('')
  const [workedTo, setWorkedTo] = useState('')
  /** Empty for everyone; a real id otherwise. Unassigned time has no id to pick. */
  const [attorneyId, setAttorneyId] = useState('')
  const [billStatus, setBillStatus] = useState<BillStatus | ''>('')
  const [page, setPage] = useState(1)
  const [resolving, setResolving] = useState<string | null>(null)

  /**
   * A Clio Custom Action lands here with Clio's own identifiers (KAN-54). They
   * are held in the URL rather than in state so the link is shareable and
   * survives a refresh — which it has to, because a refresh signs you out and
   * the deep link is carried back through sign-in.
   */
  const clioActivity = searchParams.get('clioActivity')
  const clioMatter = searchParams.get('clioMatter')
  const fromClio = Boolean(clioActivity || clioMatter)

  const query = new URLSearchParams({ page: String(page) })
  if (type) query.set('type', type)
  if (workedFrom) query.set('from', workedFrom)
  if (workedTo) query.set('to', workedTo)
  if (attorneyId) query.set('attorneyId', attorneyId)
  if (billStatus) query.set('billStatus', billStatus)
  /**
   * Following a link from Clio shows every status: the exception may well have
   * been resolved a minute ago by somebody else, and "no results" would read as
   * "there was never a problem".
   */
  query.set('status', fromClio ? 'all' : status)
  if (clioActivity) query.set('clioActivityId', clioActivity)
  if (clioMatter) query.set('clioMatterId', clioMatter)

  const exceptions = useQuery({
    queryKey: [
      'billing-exceptions',
      type,
      status,
      workedFrom,
      workedTo,
      attorneyId,
      billStatus,
      page,
      clioActivity,
      clioMatter,
    ],
    queryFn: () => apiFetch<ExceptionPage>(`/billing-exceptions?${query.toString()}`),
  })

  const summary = useQuery({
    queryKey: ['billing-exceptions-summary'],
    queryFn: () => apiFetch<Summary>('/billing-exceptions/summary'),
  })

  const detect = useMutation({
    mutationFn: () =>
      apiFetch<DetectionResult>('/billing-exceptions/detect', { method: 'POST' }),
    onSuccess: () => invalidate(),
  })

  const resolve = useMutation({
    mutationFn: (input: { id: string; resolution: HumanResolution; note: string }) =>
      apiFetch<ExceptionRow>(`/billing-exceptions/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          resolution: input.resolution,
          note: input.note.trim() || undefined,
        }),
      }),
    onSuccess: () => {
      setResolving(null)
      void invalidate()
    },
  })

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['billing-exceptions'] })
    void queryClient.invalidateQueries({ queryKey: ['billing-exceptions-summary'] })
    /** The forecast holds blocked time out of WIP, so it moved too. */
    void queryClient.invalidateQueries({ queryKey: ['billing-forecast'] })
  }

  function changeFilter(apply: () => void) {
    apply()
    setPage(1)
    setResolving(null)
  }

  function clearClioFilter() {
    setSearchParams({}, { replace: true })
    setPage(1)
  }

  const rows = exceptions.data?.exceptions ?? []
  const meta = exceptions.data?.meta
  const scope = exceptions.data?.scope
  const counts = summary.data?.counts

  const mayResolve = may('billing_exceptions.resolve')

  return (
    <>
      <PageHeader
        title="Billing exceptions"
        description="Findings on unbilled time, raised automatically after every sync. Time with no matter is held out of the billing forecast until it is assigned."
        actions={
          mayResolve && (
            <Button
              onClick={() => detect.mutate()}
              disabled={detect.isPending}
              title="Detection already runs after each sync; this is for when you have just fixed something in Clio"
            >
              <SyncIcon />
              {detect.isPending ? 'Checking…' : 'Check again'}
            </Button>
          )
        }
      />

      {detect.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(detect.error)}
        </Alert>
      )}

      {detect.data && (
        <Alert tone="info" className="mb-4">
          Scanned {detect.data.entriesScanned} unbilled and draft{' '}
          {detect.data.entriesScanned === 1 ? 'entry' : 'entries'}: {detect.data.opened} raised,{' '}
          {detect.data.reopened} reopened, {detect.data.autoResolved} closed as corrected.
          {/*
            * Said on every pass that has any, because it is the difference
            * between "every rate is right" and "we had nothing to check them
            * against" — which look identical from an empty queue.
            */}
          {detect.data.ratesUnmatched > 0 && (
            <>
              {' '}
              {detect.data.ratesUnmatched}{' '}
              {detect.data.ratesUnmatched === 1 ? 'entry' : 'entries'} had no agreed rate to
              compare against, so the retainer check said nothing about them — the matter has
              no rate table in Clio, or none naming that timekeeper.
            </>
          )}
        </Alert>
      )}

      {resolve.error && (
        <Alert tone="error" className="mb-4">
          {resolve.error instanceof ApiError && resolve.error.code === 'already_resolved'
            ? 'Somebody else resolved that one first. The list has been refreshed.'
            : messageFor(resolve.error)}
        </Alert>
      )}

      <StatRow className="mb-6">
        <Stat
          label="Open"
          value={summary.isPending ? '—' : (summary.data?.open ?? 0)}
          hint={
            scope && !scope.all
              ? 'Your matters, and unassigned time you recorded.'
              : 'Across every matter in the firm.'
          }
        />
        <Stat
          label="Blocked from billing"
          value={formatMoney(summary.data?.blockedMinor ?? 0)}
          tone={(summary.data?.blockedMinor ?? 0) > 0 ? 'warning' : 'default'}
          hint="Billable time with no matter. Held out of the forecast entirely."
        />
        <Stat
          label="Rate differences"
          value={counts ? counts.rate_mismatch.open + counts.inconsistent_rate.open : '—'}
          hint="Against the agreed rate on the matter, plus rates inconsistent for one timekeeper."
        />
        <Stat
          label="Last checked"
          value={formatRelative(summary.data?.lastDetectedAt, 'not yet')}
          tone="muted"
          hint="Detection runs after every sync that could change the answer."
        />
      </StatRow>

      {fromClio && (
        <Alert tone="info" className="mb-4">
          Showing the {clioActivity ? 'time entry' : 'matter'} you followed from Clio, at every
          status.{' '}
          <button type="button" onClick={clearClioFilter} className="underline">
            Show the whole queue
          </button>
        </Alert>
      )}

      {!fromClio && (
        <Card className="mb-6">
          <CardBody>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <Field id="filter-type" label="Finding">
                <Select
                  id="filter-type"
                  value={type}
                  onChange={(event) => changeFilter(() => setType(event.target.value))}
                >
                  <option value="">Every kind</option>
                  {BILLING_EXCEPTION_TYPES.map((option) => (
                    <option key={option} value={option}>
                      {BILLING_EXCEPTION_META[option].label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field
                id="filter-status"
                label="Status"
                hint="Resolved findings are kept, with who cleared them."
              >
                <Select
                  id="filter-status"
                  value={status}
                  onChange={(event) =>
                    changeFilter(() => setStatus(event.target.value as typeof status))
                  }
                >
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="all">Both</option>
                </Select>
              </Field>

              {/*
                * The timekeeper who *recorded* the time, not the matter's
                * responsible attorney. The firm's purpose for this filter is
                * handing somebody their own list to go and fix, and those two
                * are different people often enough to matter.
                *
                * Options come from the summary rather than a user list, so the
                * only names offered are ones with something open — a dropdown
                * of forty people, thirty-eight of whom return nothing, is worse
                * than no dropdown.
                */}
              <Field
                id="filter-attorney"
                label="Timekeeper"
                hint="Only people with something open are listed."
              >
                <Select
                  id="filter-attorney"
                  value={attorneyId}
                  onChange={(event) =>
                    changeFilter(() => setAttorneyId(event.target.value))
                  }
                >
                  <option value="">Everyone</option>
                  {(summary.data?.byAttorney ?? [])
                    .filter((entry) => entry.attorneyId)
                    .map((entry) => (
                      <option key={entry.attorneyId} value={entry.attorneyId!}>
                        {entry.name ?? 'Unnamed'} ({entry.open})
                      </option>
                    ))}
                </Select>
              </Field>

              {/*
                * Where the entry stands relative to a bill. `billed` is offered
                * and returns nothing — detection never scans issued bills —
                * which the hint says rather than leaving somebody to conclude
                * the filter is broken.
                */}
              <Field
                id="filter-bill-status"
                label="Invoice status"
                hint="Findings are only raised on unbilled and draft time."
              >
                <Select
                  id="filter-bill-status"
                  value={billStatus}
                  onChange={(event) =>
                    changeFilter(() => setBillStatus(event.target.value as BillStatus | ''))
                  }
                >
                  <option value="">Any</option>
                  {BILL_STATUSES.map((option) => (
                    <option key={option} value={option}>
                      {BILL_STATUS_META[option].label}
                    </option>
                  ))}
                </Select>
              </Field>

              {/*
                * The work date, not the date the finding was raised: a backfill
                * stamps every finding with the same afternoon, so filtering on
                * that would answer a question nobody is asking. Both ends are
                * inclusive, and each bounds the other so a range cannot be
                * entered backwards.
                */}
              <Field
                id="filter-from"
                label="Worked from"
                hint="Both dates are included."
              >
                <TextInput
                  id="filter-from"
                  type="date"
                  value={workedFrom}
                  max={workedTo || undefined}
                  onChange={(event) =>
                    changeFilter(() => setWorkedFrom(event.target.value))
                  }
                />
              </Field>

              <Field
                id="filter-to"
                label="Worked to"
                hint="Leave either blank for open-ended."
              >
                <TextInput
                  id="filter-to"
                  type="date"
                  value={workedTo}
                  min={workedFrom || undefined}
                  onChange={(event) => changeFilter(() => setWorkedTo(event.target.value))}
                />
              </Field>
            </div>

            {(billStatus === 'billed' ||
              (billStatus && !DETECTED_BILL_STATUSES.includes(billStatus as never))) && (
              <Alert tone="info" className="mt-4" title="Nothing is raised on billed time">
                A finding is a thing somebody is going to go and fix, and work already on an
                issued bill can only be credited. The filter keeps the option so the three
                states read as a complete set.
              </Alert>
            )}

            {(workedFrom || workedTo || attorneyId || billStatus) && (
              <div className="mt-4">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    changeFilter(() => {
                      setWorkedFrom('')
                      setWorkedTo('')
                      setAttorneyId('')
                      setBillStatus('')
                    })
                  }
                >
                  Clear filters
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {!fromClio && (summary.data?.byAttorney.length ?? 0) > 1 && (
        <Card className="mb-6">
          <CardHeader
            title="By timekeeper"
            description="Open findings per person, biggest first. Pick one to work their list, or to have something concrete to send them."
          />
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {(summary.data?.byAttorney ?? []).map((entry) => {
                const selected = entry.attorneyId !== null && entry.attorneyId === attorneyId

                return (
                  <button
                    key={entry.attorneyId ?? 'unassigned'}
                    type="button"
                    /*
                     * Unassigned time is shown and is not selectable: there is
                     * no id to filter on, and the entries are already in the
                     * queue under "no matter assigned". Hiding the row would
                     * hide the findings nobody is going to be told about.
                     */
                    disabled={entry.attorneyId === null}
                    onClick={() =>
                      changeFilter(() =>
                        setAttorneyId(selected ? '' : (entry.attorneyId ?? ''))
                      )
                    }
                    className={[
                      'rounded-lg border px-3 py-2 text-left text-sm transition',
                      entry.attorneyId === null
                        ? 'cursor-default border-ink-100 bg-ink-50 text-ink-600'
                        : selected
                          ? 'border-brand-500 bg-brand-50 text-brand-900'
                          : 'border-ink-200 hover:border-brand-300 hover:bg-brand-50/40',
                    ].join(' ')}
                  >
                    <span className="block font-medium">
                      {entry.name ?? 'Nobody assigned'}
                    </span>
                    <span className="block text-xs text-ink-600">
                      {entry.open} open · {formatMoney(entry.amountMinor)}
                    </span>
                  </button>
                )
              })}
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Findings"
          description={
            exceptions.isPending
              ? 'Loading…'
              : meta
                ? `${meta.total} ${meta.total === 1 ? 'finding' : 'findings'}`
                : undefined
          }
        />

        {exceptions.error ? (
          <CardBody>
            <Alert tone="error">{messageFor(exceptions.error)}</Alert>
          </CardBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Finding</Th>
                <Th>Matter</Th>
                <Th>Entry</Th>
                <Th className="text-right">Value</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {exceptions.isPending && <EmptyRow colSpan={6}>Loading findings…</EmptyRow>}

              {!exceptions.isPending && rows.length === 0 && (
                <EmptyRow colSpan={6}>
                  {status === 'open' && !type && !workedFrom && !workedTo && !fromClio
                    ? 'Nothing open. Every unbilled entry in your scope passes the checks.'
                    : 'Nothing matches these filters.'}
                </EmptyRow>
              )}

              {rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  mayResolve={mayResolve}
                  resolving={resolving === row.id}
                  pending={resolve.isPending}
                  onResolveOpen={() => setResolving(resolving === row.id ? null : row.id)}
                  onResolve={(resolution, note) =>
                    resolve.mutate({ id: row.id, resolution, note })
                  }
                />
              ))}
            </tbody>
          </TableWrap>
        )}

        {meta && meta.lastPage > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-5 py-3">
            <p className="text-sm text-ink-600">
              Page {meta.currentPage} of {meta.lastPage}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={meta.currentPage <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                disabled={meta.currentPage >= meta.lastPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <RuleSettings canEdit={may('billing_exceptions.resolve')} />

      <p className="mt-6 max-w-2xl text-xs leading-relaxed text-ink-600">
        Findings are raised by rule, not by judgement: a rate below the retainer is often an agreed
        discount, and marking one <em>reviewed and accepted</em> is the answer for that. Marking one{' '}
        <em>corrected</em> is a claim about the data — if the next check still finds it, it comes
        back. See{' '}
        <Link to="/roles" className="text-brand-700 underline">
          roles &amp; permissions
        </Link>{' '}
        for who can do which.
      </p>
    </>
  )
}

function Row({
  row,
  mayResolve,
  resolving,
  pending,
  onResolveOpen,
  onResolve,
}: {
  row: ExceptionRow
  mayResolve: boolean
  resolving: boolean
  pending: boolean
  onResolveOpen: () => void
  onResolve: (resolution: HumanResolution, note: string) => void
}) {
  const meta = BILLING_EXCEPTION_META[row.exceptionType]

  return (
    <>
      <tr className={resolving ? 'bg-brand-50/50' : undefined}>
        <Td>
          <Badge tone={TYPE_TONES[row.exceptionType]}>{meta.label}</Badge>
          {/*
            * Only duplicate detection carries a confidence, and showing it
            * beside the type is what lets somebody work the near-certain ones
            * first without opening each row.
            */}
          {row.confidence && (
            <span className="ml-1">
              <Badge tone={row.confidence === 'high' ? 'critical' : 'neutral'}>
                {BILLING_EXCEPTION_CONFIDENCE_LABELS[row.confidence]}
              </Badge>
            </span>
          )}
          <span className="mt-1 block text-xs text-ink-600">{detailFor(row)}</span>
        </Td>

        <Td>
          {row.matter ? (
            <>
              <span className="block text-ink-900">{row.matter.displayNumber}</span>
              <span className="block text-xs text-ink-600">
                {row.matter.clientName ?? 'Client not synced'}
              </span>
            </>
          ) : (
            <span className="text-accent-700">None assigned</span>
          )}
        </Td>

        <Td>
          <span className="block text-ink-900">
            {formatDateTime(row.timeEntry?.workedAt ?? null)}
          </span>
          <span className="block text-xs text-ink-600">
            {formatHours(row.timeEntry?.durationSeconds)}
            {row.attorney ? ` · ${row.attorney.name}` : ''}
          </span>
          {/*
            * Only worth a badge when it is not the default. Marking every row
            * "Unbilled" would be a column of the same word, and the one thing
            * this is for is spotting the entries already gathered onto a draft.
            */}
          {row.timeEntry && row.timeEntry.billStatus !== 'unbilled' && (
            <span className="mt-1 block">
              <Badge tone={BILL_STATUS_TONES[row.timeEntry.billStatus]}>
                {BILL_STATUS_META[row.timeEntry.billStatus].label}
                {row.timeEntry.invoiceNumber ? ` ${row.timeEntry.invoiceNumber}` : ''}
              </Badge>
            </span>
          )}
        </Td>

        <Td className="text-right tabular-nums">{formatMoney(row.amountMinor)}</Td>

        <Td>
          {row.status === 'open' ? (
            <Badge tone="neutral">Open</Badge>
          ) : (
            <>
              <Badge tone="positive">
                {row.resolution
                  ? BILLING_EXCEPTION_RESOLUTION_META[row.resolution].label
                  : 'Resolved'}
              </Badge>
              <span className="mt-1 block text-xs text-ink-600">
                {row.resolvedByName ?? 'Automatically'} · {formatRelative(row.resolvedAt)}
              </span>
            </>
          )}
        </Td>

        <Td className="text-right">
          {row.status === 'open' && mayResolve && (
            <Button size="sm" variant="ghost" onClick={onResolveOpen} aria-expanded={resolving}>
              {resolving ? 'Cancel' : 'Resolve'}
            </Button>
          )}
        </Td>
      </tr>

      {resolving && (
        <tr>
          <td colSpan={6} className="border-b border-ink-200 bg-ink-50 px-5 py-4">
            <ResolveForm row={row} pending={pending} onResolve={onResolve} />
          </td>
        </tr>
      )}

      {row.status === 'resolved' && row.resolutionNote && (
        <tr>
          <td colSpan={6} className="border-b border-ink-100 bg-ink-50/60 px-5 py-2 text-xs text-ink-700">
            <span className="font-semibold">Note: </span>
            {row.resolutionNote}
          </td>
        </tr>
      )}
    </>
  )
}

function ResolveForm({
  row,
  pending,
  onResolve,
}: {
  row: ExceptionRow
  pending: boolean
  onResolve: (resolution: HumanResolution, note: string) => void
}) {
  const [resolution, setResolution] = useState<HumanResolution>('corrected')
  const [note, setNote] = useState('')

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onResolve(resolution, note)
      }}
    >
      <p className="mb-3 max-w-2xl text-sm text-ink-700">{BILLING_EXCEPTION_META[row.exceptionType].description}</p>

      {row.timeEntry?.sourceId && (
        <p className="mb-3 text-xs text-ink-600">
          Clio {row.timeEntry.sourceSystem === 'clio' ? 'activity' : 'record'}{' '}
          <code className="font-mono">{row.timeEntry.sourceId}</code>
          {row.matter?.sourceId && (
            <>
              {' '}
              on matter <code className="font-mono">{row.matter.sourceId}</code>
            </>
          )}
          . Fix it there, then choose <em>corrected</em> here.
        </p>
      )}

      <AgreedRates row={row} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id={`resolution-${row.id}`}
          label="Outcome"
          hint={BILLING_EXCEPTION_RESOLUTION_META[resolution].description}
        >
          <Select
            id={`resolution-${row.id}`}
            value={resolution}
            onChange={(event) => setResolution(event.target.value as HumanResolution)}
          >
            {HUMAN_RESOLUTIONS.map((option) => (
              <option key={option} value={option}>
                {BILLING_EXCEPTION_RESOLUTION_META[option].label}
              </option>
            ))}
          </Select>
        </Field>

        <Field id={`note-${row.id}`} label="Note" hint="Optional, and the part worth reading later.">
          <TextInput
            id={`note-${row.id}`}
            value={note}
            maxLength={1000}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Agreed discount, confirmed with the client"
          />
        </Field>
      </div>

      <div className="mt-3">
        <Button type="submit" variant="accent" size="sm" disabled={pending}>
          {pending ? 'Saving…' : 'Resolve'}
        </Button>
      </div>
    </form>
  )
}

/**
 * The matter's agreed rate table, shown while resolving a rate finding.
 *
 * This is the firm's own ask — "ability to ... read through the matter
 * dashboard in this section" — and it is the difference between a finding
 * somebody can act on and one they have to go and verify in Clio first. The
 * question a rate flag provokes is "says who?", and the answer is this table.
 *
 * Shown **whole**, including the rows that were not matched. A role rate the
 * platform cannot attach to a person is exactly what a reviewer needs to see,
 * because it explains why an entry that looks priced correctly was flagged, or
 * why one that looks wrong was not.
 *
 * Only on the two rate findings. On a missing description the table is noise.
 */
function AgreedRates({ row }: { row: ExceptionRow }) {
  if (row.exceptionType !== 'rate_mismatch' && row.exceptionType !== 'inconsistent_rate') {
    return null
  }
  if (!row.matter) return null

  const rates = row.matter.rates

  return (
    <div className="mb-3 max-w-2xl rounded-lg border border-ink-200 bg-white p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-600">
        Agreed rates on {row.matter.displayNumber} · {MATTER_FEE_TYPE_LABELS[row.matter.feeType]}
      </p>

      {rates.length === 0 ? (
        <p className="text-sm text-ink-700">
          No rate table has synced for this matter, so there is nothing to compare an entry
          against. Set the custom billing rates on the matter in Clio and they will appear here
          on the next sync.
        </p>
      ) : (
        <ul className="space-y-1 text-sm">
          {rates.map((rate, index) => (
            <li key={index} className="flex justify-between gap-4">
              <span className="text-ink-700">
                {rate.attorneyName ?? rate.roleLabel ?? MATTER_RATE_SUBJECT_LABELS.matter_default}
                {rate.subject === 'role' && (
                  /*
                   * Named as unmatched rather than left to look applied. A
                   * reviewer seeing "Paralegal $250" would reasonably assume it
                   * was used; it is not, because nothing here knows who is in
                   * that Clio group.
                   */
                  <span className="ml-1 text-xs text-ink-500">
                    (role — not matched to a person)
                  </span>
                )}
              </span>
              <span className="tabular-nums text-ink-900">{formatMoney(rate.rateMinor)}/h</span>
            </li>
          ))}
        </ul>
      )}

      {rates.length > 0 && !row.matter.hasAgreedRateForAttorney && (
        <p className="mt-2 text-xs text-ink-600">
          None of these applies to {row.attorney?.name ?? 'this timekeeper'}, so the retainer
          comparison said nothing about this entry.
        </p>
      )}
    </div>
  )
}

/**
 * The one line that says what is actually wrong with this entry.
 *
 * Reads the rule's own `detail` payload where there is one, so the row states
 * *which* check tripped rather than the rule's general description. "Too short
 * to bill against — 4 characters, minimum 10" is actionable; "no narrative" on
 * an entry that has one reads as a bug.
 */
function detailFor(row: ExceptionRow): string {
  switch (row.exceptionType) {
    case 'rate_mismatch': {
      /*
       * Which rung of the matter's rate table was matched, because "the agreed
       * rate" is a different claim depending on the answer — a rate set against
       * this person by name is harder to argue with than the matter's default.
       */
      const subject = row.detail?.agreedRateSubject as MatterRateSubject | undefined
      const against =
        subject === 'matter_default'
          ? "the matter's default rate"
          : subject === 'role'
            ? 'the rate agreed for their role'
            : 'the rate agreed for them on this matter'

      return `Billed at ${formatMoney(row.actualRateMinor)}/h against ${against} of ${formatMoney(
        row.expectedRateMinor
      )}/h`
    }
    case 'missing_description': {
      const flag = row.detail?.flag as DescriptionFlag | undefined
      if (flag === 'placeholder') return 'Placeholder text, not a narrative'
      if (flag === 'too_short') {
        const length = String(row.detail?.description ?? '').trim().length
        return `Too short to bill against — ${length} characters, minimum ${row.detail?.minLength}`
      }
      return 'No narrative on the entry'
    }
    case 'possible_duplicate': {
      const when = row.duplicateOf
        ? `the entry of ${formatDateTime(row.duplicateOf.workedAt)}`
        : 'an earlier entry'
      return row.confidence === 'high'
        ? `Identical to ${when}, description included`
        : `Same attorney, day, matter and duration as ${when}, with a different description`
    }
    case 'daily_hours_exceeded': {
      const hours = Number(row.detail?.totalSeconds ?? 0) / 3600
      return `${hours.toFixed(1)} h logged on ${row.detail?.day} across ${row.detail?.entryCount} entries, against a limit of ${row.detail?.limitHours} h`
    }
    case 'no_matter_assigned':
      return 'Cannot be billed to anyone until a matter is set'
    case 'inconsistent_rate': {
      const atUsual = Number(row.detail?.entriesAtUsualRate ?? 0)
      const total = Number(row.detail?.entriesOnMatter ?? 0)
      return (
        `Billed at ${formatMoney(row.actualRateMinor)}/h where ${atUsual} of ${total} ` +
        `entries by the same person on this matter are at ${formatMoney(row.expectedRateMinor)}/h`
      )
    }
    case 'wip_below_threshold': {
      const entries = Number(row.detail?.entryCount ?? 0)
      return (
        `${formatMoney(Number(row.detail?.wipMinor ?? 0))} unbilled across ${entries} ` +
        `${entries === 1 ? 'entry' : 'entries'}, under the ` +
        `${formatMoney(Number(row.detail?.thresholdMinor ?? 0))} the firm bills for`
      )
    }
  }
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}

/**
 * The thresholds the rules read (KAN-73 / KAN-74).
 *
 * On this page rather than a settings screen of its own, because the numbers
 * are only meaningful beside the findings they produced: somebody looking at
 * six "too short" rows should be able to see the minimum, decide it is wrong,
 * and change it without navigating away.
 *
 * Both tickets expect Finance to tune these after seeing the first real batch,
 * which is the whole reason they are in the database rather than in code.
 */
function RuleSettings({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<BillingRuleSettings | null>(null)
  const [error, setError] = useState<string | null>(null)

  const settings = useQuery({
    queryKey: ['billing-exception-settings'],
    queryFn: () => apiFetch<BillingRuleSettings>('/billing-exceptions/settings'),
  })

  const save = useMutation({
    mutationFn: (next: BillingRuleSettings) =>
      apiFetch<BillingRuleSettings>('/billing-exceptions/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          minDescriptionLength: next.minDescriptionLength,
          dailyHoursLimit: next.dailyHoursLimit,
          minWipMinor: next.minWipMinor,
          placeholderTerms: next.placeholderTerms,
        }),
      }),
    onSuccess: async () => {
      setDraft(null)
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['billing-exception-settings'] })
      /** The queue is downstream of these, so it is stale the moment they change. */
      await queryClient.invalidateQueries({ queryKey: ['billing-exceptions'] })
    },
    onError: (saveError: Error) => setError(messageFor(saveError)),
  })

  const current = draft ?? settings.data ?? null

  return (
    <Card className="mt-6">
      <CardHeader
        title="Rule thresholds"
        description="What counts as too short, what counts as placeholder text, and how long a day has to be before it is worth a look. Changing these takes effect on the next detection pass — no deployment."
        actions={
          canEdit &&
          draft && (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="accent"
                disabled={save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )
        }
      />

      <CardBody>
        {settings.isPending && <p className="text-sm text-ink-600">Loading…</p>}
        {error && (
          <Alert tone="error" className="mb-3">
            {error}
          </Alert>
        )}

        {current && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              id="min-description"
              label="Minimum description length"
              hint="Characters. Below this, an entry is flagged as too short to bill against."
            >
              <TextInput
                id="min-description"
                inputMode="numeric"
                disabled={!canEdit}
                value={String(current.minDescriptionLength)}
                onChange={(event) =>
                  setDraft({
                    ...current,
                    minDescriptionLength: Number(event.target.value) || 0,
                  })
                }
              />
            </Field>

            <Field
              id="daily-hours"
              label="Daily hours limit"
              hint="More than this in one day raises its own finding — not a duplicate, because the fix is different."
            >
              <TextInput
                id="daily-hours"
                inputMode="numeric"
                disabled={!canEdit}
                value={String(current.dailyHoursLimit)}
                onChange={(event) =>
                  setDraft({ ...current, dailyHoursLimit: Number(event.target.value) || 0 })
                }
              />
            </Field>

            {/*
              * Entered in dollars and stored in minor units. Everything else on
              * this card is a count, so a field that silently wanted cents
              * would be wrong by a hundred every time — and the one place that
              * shows up is a queue that suddenly flags every matter.
              */}
            <Field
              id="min-wip"
              label="Minimum worth billing"
              hint="Dollars. A matter with less unbilled than this is flagged as better deferred — usually a newly opened file."
            >
              <TextInput
                id="min-wip"
                inputMode="decimal"
                disabled={!canEdit}
                value={String(current.minWipMinor / 100)}
                onChange={(event) =>
                  setDraft({
                    ...current,
                    minWipMinor: Math.round((Number(event.target.value) || 0) * 100),
                  })
                }
              />
            </Field>

            <Field
              id="placeholders"
              label="Placeholder terms"
              hint="Comma separated. Matched against the whole description, never as a substring — “Reviewed the TBD schedule” is a real narrative."
            >
              <TextInput
                id="placeholders"
                disabled={!canEdit}
                value={current.placeholderTerms.join(', ')}
                onChange={(event) =>
                  setDraft({
                    ...current,
                    placeholderTerms: event.target.value.split(',').map((term) => term.trim()),
                  })
                }
              />
            </Field>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
